/**
 * TimeEntries — raw attendance records across the tenant.
 *
 * Migrated to live API in Wave 3.2. Reads GET /v1/attendance/time-entries
 * with backend-side filters (start_date, end_date, department_id, status,
 * worker free-text). Departments come from the organization endpoint
 * so the filter dropdown matches the tenant's actual structure rather
 * than a hardcoded list.
 *
 * Shape differences from the prior mock:
 *   - Backend clock_in / clock_out arrive as full datetimes; we slice
 *     the HH:MM suffix for the time column but keep the datetime in
 *     the detail dialog so the operator can confirm the date too.
 *   - Backend status/source enums are lowercase underscored
 *     (early_leave vs the mock's 'Early Leave'); the display layer
 *     normalizes them per the mock's title-case presentation.
 *   - GeoInfo from the backend is lat/lng/address/within_geofence only.
 *     The mock's `distance` and `method` fields don't exist in the
 *     response so those rows are simply omitted from the dialog.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CheckCircleOutlined, CloseOutlined, DesktopWindowsOutlined,
  EditOutlined, GpsFixedOutlined, InfoOutlined,
  PhoneIphoneOutlined, PhotoCameraOutlined, RefreshOutlined, WarningAmberOutlined,
  LocationOnOutlined,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type { GeoInfo, ManualEntryCreate, TimeEntry, TimePunch, OrgPunch } from '../../../api/attendance';
import type { WorkerResponse } from '../../../api/organization';
import PhotoViewerDialog from '../../../components/PhotoViewerDialog';
import LocationViewerDialog, { type ViewerFence } from '../../../components/location/LocationViewerDialog';
import { Link as MuiLink } from '@mui/material';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { activeLocale, fmtUnit } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';
import { ExportButton } from '../../../components/primitives';
// ---------------------------------------------------------------------------
// Period helpers — match the ScheduleView / Approvals UX.
// ---------------------------------------------------------------------------

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function monthStart() { const d = new Date(); d.setDate(1); return isoDate(d); }
function todayIso() { return isoDate(new Date()); }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }

const periodOptions = [
  { value: 'today', i18nKey: 'attendance:timeEntries.periods.today' },
  { value: 'yesterday', i18nKey: 'attendance:timeEntries.periods.yesterday' },
  { value: 'this_week', i18nKey: 'attendance:timeEntries.periods.thisWeek' },
  { value: 'last_week', i18nKey: 'attendance:timeEntries.periods.lastWeek' },
  { value: 'this_month', i18nKey: 'attendance:timeEntries.periods.thisMonth' },
];

function periodRange(v: string): [string, string] | null {
  const today = new Date();
  const dow = today.getDay();
  const offset = dow === 0 ? 6 : dow - 1;
  switch (v) {
    case 'today': return [todayIso(), todayIso()];
    case 'yesterday': return [daysAgo(1), daysAgo(1)];
    case 'this_week': return [daysAgo(offset), todayIso()];
    case 'last_week': return [daysAgo(offset + 7), daysAgo(offset + 1)];
    case 'this_month': return [monthStart(), todayIso()];
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function timeOnly(dt: string | null | undefined): string {
  if (!dt) return '';
  // Handles "2026-04-22T09:15:00+00:00" or "09:15".
  const m = dt.match(/T(\d{2}:\d{2})/);
  if (m) return m[1];
  const m2 = dt.match(/^(\d{2}:\d{2})/);
  return m2 ? m2[1] : dt.slice(0, 5);
}

const STATUS_OPTIONS = [
  { value: 'all', i18nKey: 'attendance:timeEntries.statuses.all' },
  { value: 'normal', i18nKey: 'attendance:timeEntries.statuses.normal' },
  { value: 'late', i18nKey: 'attendance:timeEntries.statuses.late' },
  { value: 'early_leave', i18nKey: 'attendance:timeEntries.statuses.earlyLeave' },
  { value: 'absent', i18nKey: 'attendance:timeEntries.statuses.absent' },
  { value: 'incomplete', i18nKey: 'attendance:timeEntries.statuses.incomplete' },
  { value: 'business_trip', i18nKey: 'attendance:timeEntries.statuses.businessTrip' },
];

function statusDisplay(s: string | null | undefined, t: (key: string, o?: Record<string, unknown>) => string): string {
  const found = STATUS_OPTIONS.find((o) => o.value === s);
  if (found) return t(found.i18nKey);
  if (!s) return '—';
  // The row status is the day summary's, whose vocabulary is wider than this page's
  // filter (pending_review, short, half_day, leave …). Its labels already exist, in all
  // nine locales, for the reports; reuse them rather than printing the raw code.
  return t(`attendance:attendanceReports.values.status.${s}`, { defaultValue: s });
}

function statusColor(s: string | null | undefined): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (s === 'normal') return 'success';
  if (s === 'absent') return 'error';
  if (s === 'late' || s === 'early_leave') return 'warning';
  // An approved trip is authorised, not an exception — the same reading `leave` gets in
  // the reports colour scale. Deliberately NOT 'error': it used to arrive as 'absent'.
  if (s === 'business_trip') return 'info';
  return 'default';
}

function sourceIcon(s: string | null | undefined) {
  if (s === 'mobile' || s === 'Mobile') return <PhoneIphoneOutlined sx={{ fontSize: 14 }} />;
  if (s === 'kiosk' || s === 'Kiosk') return <DesktopWindowsOutlined sx={{ fontSize: 14 }} />;
  return <EditOutlined sx={{ fontSize: 14 }} />;
}

function sourceLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Geolocation sub-component
// ---------------------------------------------------------------------------

const GeoDetail: React.FC<{
  label: string;
  time?: string;
  geo?: GeoInfo | null;
  onViewMap?: () => void;
}> = ({ label, time, geo, onViewMap }) => {
  const { t } = useTranslation();
  return (
  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GpsFixedOutlined sx={{ fontSize: 14, color: geo ? 'primary.main' : 'text.disabled' }} />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{label}</Typography>
        {time && <Typography variant="caption" color="text.secondary">({time})</Typography>}
      </Box>
      {geo && (
        geo.within_geofence
          ? <Chip icon={<CheckCircleOutlined sx={{ fontSize: 12 }} />} label={t('attendance:timeEntries.inZone')} size="small" color="success" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
          : <Chip icon={<WarningAmberOutlined sx={{ fontSize: 12 }} />} label={t('attendance:timeEntries.outOfZone')} size="small" color="error" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
      )}
    </Box>
    {!geo ? (
      <Typography variant="caption" color="text.disabled">{t('attendance:timeEntries.noLocation')}</Typography>
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr', rowGap: 0.75, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t('attendance:timeEntries.address')}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>{geo.address ?? '—'}</Typography>
        <Typography variant="caption" color="text.secondary">{t('attendance:timeEntries.coordinates')}</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
          {geo.lat != null && geo.lng != null
            ? `${Number(geo.lat).toFixed(6)}, ${Number(geo.lng).toFixed(6)}`
            : '—'}
        </Typography>
      </Box>
    )}
    {geo && geo.lat != null && geo.lng != null && onViewMap && (
      <Button
        size="small"
        startIcon={<LocationOnOutlined sx={{ fontSize: 14 }} />}
        onClick={onViewMap}
        sx={{ mt: 1, textTransform: 'none', fontSize: '0.72rem', borderRadius: 1 }}
      >
        {t('attendance:timeEntries.viewOnMap')}
      </Button>
    )}
  </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DepartmentLite {
  id: string;
  name: string;
}

interface LocationLite {
  id: string;
  name: string;
}

const TimeEntries: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [depts, setDepts] = useState<DepartmentLite[]>([]);
  const [locations, setLocations] = useState<LocationLite[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Tab: 'timesheets' (per-day summary) vs 'events' (punch-level flow).
  // URL can pre-seed: /attendance/time-entries?view=events
  const initialTab: 'timesheets' | 'events' =
    searchParams.get('view') === 'events' ? 'events' : 'timesheets';
  const [tab, setTab] = useState<'timesheets' | 'events'>(initialTab);

  // URL can pre-seed the worker filter: /attendance/time-entries?worker=Sarah
  // Used by ActiveWorkers' "View attendance" action to land here with the
  // relevant person already searched.
  const urlWorker = searchParams.get('worker') ?? '';

  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(todayIso());
  const [quickPeriod, setQuickPeriod] = useState('this_month');
  const [deptFilter, setDeptFilter] = useState('all');
  const [locFilter, setLocFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [punchFilter, setPunchFilter] = useState<'all' | 'valid' | 'invalid'>('all');
  const [orgPunches, setOrgPunches] = useState<OrgPunch[]>([]);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'mobile' | 'web' | 'kiosk' | 'manual'>('all');
  const [workerFilter, setWorkerFilter] = useState(urlWorker);
  const [applied, setApplied] = useState({
    start: monthStart(),
    end: todayIso(),
    dept: 'all',
    loc: 'all',
    status: 'all',
    punch: 'all' as 'all' | 'valid' | 'invalid',
    source: 'all' as 'all' | 'mobile' | 'web' | 'kiosk' | 'manual',
    worker: urlWorker,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailEntry, setDetailEntry] = useState<TimeEntry | null>(null);
  const [detailPunches, setDetailPunches] = useState<TimePunch[]>([]);
  const [punchesLoading, setPunchesLoading] = useState(false);

  // ADR-028 — load the punch stream for the opened entry so admins see
  // every punch + which were filtered (invalid) vs selected as in/out.
  useEffect(() => {
    if (!detailEntry) { setDetailPunches([]); return; }
    let alive = true;
    setPunchesLoading(true);
    attendanceApi.timeEntriesApi
      .punches(detailEntry.id)
      .then((rows) => { if (alive) setDetailPunches(rows); })
      .catch(() => { if (alive) setDetailPunches([]); })
      .finally(() => { if (alive) setPunchesLoading(false); });
    return () => { alive = false; };
  }, [detailEntry]);
  // ADR-027 — pending clock_in_face_review requests keyed by time_entry_id,
  // so the detail dialog's Approve/Reject know which approval to patch.
  const [faceReviewByEntry, setFaceReviewByEntry] = useState<Record<string, string>>({});
  const [reviewingEntry, setReviewingEntry] = useState<string | null>(null);
  // Privacy audit Wave 5 — admin photo viewer state. Closes Finding 7
  // (dead data): clock-in selfies were captured but never displayed,
  // making "anti buddy-punching" non-functional.
  const [photoEntry, setPhotoEntry] = useState<TimeEntry | null>(null);
  const [photoUrlOverride, setPhotoUrlOverride] = useState<string | null>(null);

  // Location map popup (owner 2026-06-26 — Location column was "—" + no map).
  // Org geofences drawn as context circles; the marker is the punch's coords.
  const [locView, setLocView] = useState<
    { lat: number | null; lng: number | null; within?: boolean | null; address?: string | null; subtitle?: string } | null
  >(null);
  const [orgFences, setOrgFences] = useState<ViewerFence[]>([]);

  // Manual-entry dialog state — backs the "Add Manual Entry" button.
  // Backend: POST /v1/attendance/time-entries/manual with ManualEntryCreate.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manual, setManual] = useState<{
    worker_id: string;
    date: string;
    clock_in_time: string;  // HH:MM
    clock_out_time: string; // HH:MM, optional
    notes: string;
  }>({
    worker_id: '',
    date: todayIso(),
    clock_in_time: '09:00',
    clock_out_time: '',
    notes: '',
  });

  // ---------- data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await attendanceApi.timeEntriesApi.list({
        start_date: applied.start,
        end_date: applied.end,
        department_id: applied.dept === 'all' ? undefined : applied.dept,
        location_id: applied.loc === 'all' ? undefined : applied.loc,
        status: applied.status === 'all' ? undefined : applied.status,
        worker: applied.worker || undefined,
      });
      setEntries(rows);
      // ADR-028 — org-wide punch stream for the Clock Events tab: EVERY punch
      // in range (not the derived in/out summary). Capped; newest first.
      try {
        const punches = await attendanceApi.timeEntriesApi.orgPunches({
          start_date: applied.start,
          end_date: applied.end,
          limit: 500,
        });
        setOrgPunches(punches);
      } catch {
        setOrgPunches([]);
      }
      // ADR-027 — best-effort: map pending clock_in_face_review requests by
      // time_entry_id for the per-entry Approve/Reject actions.
      try {
        const pend = await attendanceApi.approvalsApi.list({
          type: 'clock_in_face_review',
          status: 'pending',
        });
        const m: Record<string, string> = {};
        for (const a of pend) {
          const tid = (a.data as { time_entry_id?: string } | null)?.time_entry_id;
          if (tid) m[tid] = a.id;
        }
        setFaceReviewByEntry(m);
      } catch {
        /* non-fatal — review actions just won't render */
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:timeEntries.noEntries'));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [applied, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ADR-027 — approve / reject a pending clock-in face review.
  const handleReviewFace = async (
    entry: TimeEntry,
    status: 'approved' | 'rejected',
  ) => {
    const reqId = faceReviewByEntry[entry.id];
    if (!reqId) {
      setToast(t('attendance:timeEntries.faceReview.noRequest'));
      return;
    }
    setReviewingEntry(entry.id);
    try {
      await attendanceApi.approvalsApi.update(reqId, { status });
      setToast(
        t(
          status === 'approved'
            ? 'attendance:timeEntries.faceReview.approved'
            : 'attendance:timeEntries.faceReview.rejected',
        ),
      );
      setDetailEntry(null);
      await fetchAll();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:timeEntries.faceReview.failed'),
      );
    } finally {
      setReviewingEntry(null);
    }
  };

  useEffect(() => {
    organizationApi.departmentsApi
      .list()
      .then((rows) => setDepts(rows.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setDepts([]));
    organizationApi.locationsApi
      .list()
      .then((rows) => setLocations(rows.map((l) => ({ id: l.id, name: l.name }))))
      .catch(() => setLocations([]));
    // Workers for the manual-entry dialog dropdown.
    organizationApi.workersApi
      .list('active')
      .then(setWorkers)
      .catch(() => setWorkers([]));
    // Org geofences → context circles on the location map popup.
    attendanceApi.geofencesApi
      .list()
      .then((rows) =>
        setOrgFences(
          rows
            .filter((g) => g.lat != null && g.lng != null)
            .map((g) => ({
              name: g.name,
              lat: Number(g.lat),
              lng: Number(g.lng),
              radius_m: Number(g.radius_meters ?? 200),
            })),
        ),
      )
      .catch(() => setOrgFences([]));
  }, []);

  // ---------- filters ----------
  const isDirty =
    applied.start !== startDate ||
    applied.end !== endDate ||
    applied.dept !== deptFilter ||
    applied.loc !== locFilter ||
    applied.status !== statusFilter ||
    applied.punch !== punchFilter ||
    applied.source !== sourceFilter ||
    applied.worker !== workerFilter;

  const handleApply = () => {
    setApplied({
      start: startDate, end: endDate,
      dept: deptFilter, loc: locFilter,
      status: statusFilter,
      punch: punchFilter, source: sourceFilter,
      worker: workerFilter,
    });
    // Keep URL in sync so "View attendance" deep-links stay stable across reloads.
    const next = new URLSearchParams(searchParams);
    if (workerFilter) next.set('worker', workerFilter);
    else next.delete('worker');
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  // ---------- manual entry ----------
  const canSaveManual =
    !!manual.worker_id && !!manual.date && !!manual.clock_in_time &&
    (!manual.clock_out_time || manual.clock_out_time > manual.clock_in_time);

  const handleSaveManual = async () => {
    if (!canSaveManual) return;
    setManualSaving(true);
    try {
      const payload: ManualEntryCreate = {
        worker_id: manual.worker_id,
        date: manual.date,
        // Backend expects datetime; combine date + time for each clock.
        clock_in: `${manual.date}T${manual.clock_in_time}:00Z`,
        clock_out: manual.clock_out_time
          ? `${manual.date}T${manual.clock_out_time}:00Z`
          : null,
        notes: manual.notes || null,
      };
      await attendanceApi.timeEntriesApi.manual(payload);
      setToast(t('attendance:timeEntries.manualEntryRecorded'));
      setManualOpen(false);
      setManual((m) => ({ ...m, notes: '', clock_out_time: '' }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.errorGeneric'));
    } finally {
      setManualSaving(false);
    }
  };

  // ---------- export ----------
  // Payroll + records export. This is the file an SMB hands to whoever runs payroll,
  // and the one that answers a wage dispute — FLSA keeps time records two years and
  // payroll three, four in California — so it must be complete and it must open in
  // Excel without garbling non-ASCII names. The hand-rolled version it replaces
  // escaped correctly but wrote no UTF-8 BOM, which loses exactly that.
  //
  // `entries` is the loaded, filtered set for the applied date range, which IS the
  // page's window of truth (FILTER_DESIGN_CONVENTION) — not a paginated slice of it.
  /**
   * Pending face reviews that the current date range HIDES.
   *
   * 举一反三 from the approvals page, 2026-08-10. The same defect class in a different
   * shape: these requests are fetched without a date filter, but they are only reachable
   * through a row's detail dialog — so a review attached to a punch from last month is
   * invisible while the range says "this month", and the admin has no way to know it is
   * waiting.
   *
   * The fix is NOT an unbounded default, which is right for an approval queue and wrong
   * here: a timesheet is a time-bounded document by definition and the request is
   * server-side with a row limit. So the page says how many are out of view and points
   * at the queue that shows all of them.
   */
  const hiddenFaceReviews = useMemo(() => {
    const visible = new Set(entries.map((e) => e.id));
    return Object.keys(faceReviewByEntry).filter((id) => !visible.has(id)).length;
  }, [faceReviewByEntry, entries]);

  const exportColumns = useMemo(() => [
    t('attendance:timeEntries.colDate'),
    t('attendance:timeEntries.colWorkerCode'),
    t('attendance:timeEntries.colWorker'),
    t('attendance:timeEntries.colDepartment'),
    t('attendance:timeEntries.colClockIn'),
    t('attendance:timeEntries.colClockOut'),
    t('attendance:timeEntries.colDuration'),
    t('attendance:timeEntries.colStatus'),
    t('attendance:timeEntries.colSource'),
    t('attendance:timeEntries.colLocation'),
    t('attendance:timeEntries.colCoordinates'),
  ], [t]);

  const exportRows = useMemo(() => entries.map((e) => ({
    [exportColumns[0]]: e.date ?? '',
    [exportColumns[1]]: e.worker_code ?? '',
    [exportColumns[2]]: e.worker_name ?? '',
    [exportColumns[3]]: e.department ?? '',
    [exportColumns[4]]: e.clock_in ?? '',
    [exportColumns[5]]: e.clock_out ?? '',
    [exportColumns[6]]: e.total_hours ?? '',
    [exportColumns[7]]: e.status ?? '',
    [exportColumns[8]]: e.source ?? '',
    [exportColumns[9]]: e.clock_in_geo?.address ?? '',
    // One cell, because a spreadsheet column of "lat" alone is not a place. Kept as
    // text so Excel does not round the decimals away.
    [exportColumns[10]]: e.clock_in_geo?.lat != null && e.clock_in_geo?.lng != null
      ? `${e.clock_in_geo.lat}, ${e.clock_in_geo.lng}`
      : '',
  })), [entries, exportColumns]);

  const handleQuickPeriod = (val: string) => {
    setQuickPeriod(val);
    const range = periodRange(val);
    if (range) {
      setStartDate(range[0]);
      setEndDate(range[1]);
      setApplied((p) => ({ ...p, start: range[0], end: range[1] }));
      setPage(0);
    }
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickPeriod('custom');
  };

  const paginated = useMemo(
    () => entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [entries, page, rowsPerPage],
  );

  // ---------- Clock Events data (ADR-028 real punch stream) ----------
  // One row per ACTUAL punch (not the derived in/out summary): a worker who
  // punches 5 times shows 5 rows. Sourced from GET /v1/attendance/punches.
  interface PunchEvent {
    id: string;
    worker_id: string;
    worker_name: string | null | undefined;
    worker_code: string | null | undefined;
    timestamp: string; // ISO (punched_at)
    address: string | null | undefined;
    lat: number | null | undefined;
    lng: number | null | undefined;
    within_geofence: boolean | null | undefined;
    /** Outside the fence, on a day with an approved off-site request. Distinct from
     *  `within_geofence`: that says where they were, this says it was agreed. */
    geofence_exempt: boolean;
    source: string | null | undefined;
    is_valid: boolean;
    invalid_reason: string | null | undefined;
    photo_url: string | null | undefined;
  }

  const clockEvents = useMemo<PunchEvent[]>(() => {
    const wf = applied.worker.trim().toLowerCase();
    const mapped: PunchEvent[] = orgPunches.map((p) => ({
      id: p.id,
      worker_id: p.worker_id,
      worker_name: p.worker_name,
      worker_code: p.worker_code,
      timestamp: p.punched_at,
      address: p.address,
      lat: p.lat,
      lng: p.lng,
      within_geofence: p.within_geofence,
      geofence_exempt: !!p.geofence_exempt,
      source: p.source,
      is_valid: p.is_valid ?? true,
      invalid_reason: p.invalid_reason,
      photo_url: p.photo_url,
    }));
    // Client-side filters: valid/invalid + source + worker free-text.
    // Date/range filtered by the backend via the orgPunches fetch.
    return mapped.filter((ev) => {
      if (applied.punch === 'valid' && !ev.is_valid) return false;
      if (applied.punch === 'invalid' && ev.is_valid) return false;
      if (applied.source !== 'all' && ev.source !== applied.source) return false;
      if (wf) {
        const hay = `${ev.worker_name ?? ''} ${ev.worker_code ?? ''}`.toLowerCase();
        if (!hay.includes(wf)) return false;
      }
      return true;
    });
  }, [orgPunches, applied.punch, applied.source, applied.worker]);

  const paginatedEvents = useMemo(
    () => clockEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [clockEvents, page, rowsPerPage],
  );

  const handleTabChange = (_: React.SyntheticEvent, v: 'timesheets' | 'events') => {
    setTab(v);
    setPage(0);
    const next = new URLSearchParams(searchParams);
    if (v === 'events') next.set('view', 'events');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:timeEntries.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {tab === 'timesheets'
              ? t('attendance:timeEntries.subtitleTimesheets')
              : t('attendance:timeEntries.subtitleEvents')}
          </Typography>
          {/* Outstanding work the range is hiding, and the way to reach it.
              These reviews are fetched without a date filter but are only reachable
              through a row's detail dialog, so one attached to last month's punch was
              waiting with nothing on screen to say so. Shown only when the number is
              non-zero — a line that is always there is one people stop reading. */}
          {hiddenFaceReviews > 0 && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              <MuiLink
                component={RouterLink}
                to="/attendance/approvals/face-review"
                underline="hover"
              >
                {t('attendance:timeEntries.faceReview.hiddenByRange', { count: hiddenFaceReviews })}
              </MuiLink>
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <DateField value={startDate} onChange={(e) => handleDateChange('start', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>–</Typography>
          <DateField value={endDate} onChange={(e) => handleDateChange('end', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Select value={quickPeriod} onChange={(e) => handleQuickPeriod(e.target.value)} size="small"
            sx={{ minWidth: 110, height: 32, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5, fontWeight: 600 } }}>
            {periodOptions.map((o) => <MenuItem key={o.value} value={o.value}>{t(o.i18nKey)}</MenuItem>)}
            <MenuItem value="custom">{t('attendance:timeEntries.custom')}</MenuItem>
          </Select>
          <Tooltip title={t('attendance:timeEntries.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => setManualOpen(true)}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            {t('attendance:timeEntries.addManualEntry')}
          </Button>
          <ExportButton
            filename={`time-entries-${applied.start}_to_${applied.end}`}
            title={t('attendance:timeEntries.title')}
            columns={exportColumns}
            rows={exportRows}
            onDone={() => setToast(t('attendance:timeEntries.exportDone', { count: exportRows.length }))}
            onError={(m) => setToast(m)}
          />
        </Stack>
      </Box>

      {/* View control — Segmented control (FILTER_DESIGN_CONVENTION §6).
          Timesheets (per-day summary) vs Clock Events (flat punch log). */}
      <Box sx={{ width: '100%', mb: 1.5 }}>
        <Box sx={{
          display: 'inline-flex',
          gap: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 0.375,
          bgcolor: 'background.paper',
        }}>
          {([
            { value: 'timesheets', label: t('attendance:timeEntries.tabTimesheets'), count: entries.length },
            { value: 'events', label: t('attendance:timeEntries.tabClockEvents'), count: clockEvents.length },
          ] as const).map((opt) => {
            const active = tab === opt.value;
            return (
              <Button
                key={opt.value}
                onClick={() => handleTabChange({} as React.SyntheticEvent, opt.value)}
                disableRipple
                sx={{
                  px: 2.5, py: 0.5, minHeight: 30,
                  textTransform: 'none', fontSize: '0.875rem',
                  fontWeight: active ? 600 : 500,
                  color: active ? 'primary.contrastText' : 'text.secondary',
                  bgcolor: active ? 'primary.main' : 'transparent',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: active ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                {opt.label}
                {opt.count > 0 && (
                  <Box component="span" sx={{
                    ml: 1,
                    px: 0.75, py: 0.1,
                    borderRadius: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: active ? 'rgba(255,255,255,0.25)' : 'grey.200',
                    color: active ? 'primary.contrastText' : 'text.secondary',
                    lineHeight: 1.4,
                  }}>
                    {opt.count}
                  </Box>
                )}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* Filter bar — left-to-right by FILTER_DESIGN_CONVENTION §3:
          Location (Where) → Department (Who) → Status/Punch/Source (What)
          → Worker search (Who, free-text) → Apply. */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}
        onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}>
        {/* Where */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('attendance:timeEntries.locationLabel')}</InputLabel>
          <Select value={locFilter} label={t('attendance:timeEntries.locationLabel')} onChange={(e) => setLocFilter(e.target.value)}>
            <MenuItem value="all">{t('attendance:timeEntries.allLocations')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Who */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('attendance:timeEntries.departmentLabel')}</InputLabel>
          <Select value={deptFilter} label={t('attendance:timeEntries.departmentLabel')} onChange={(e) => setDeptFilter(e.target.value)}>
            <MenuItem value="all">{t('attendance:timeEntries.allDepartments')}</MenuItem>
            {depts.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* What */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('attendance:timeEntries.statusLabel')}</InputLabel>
          <Select value={statusFilter} label={t('attendance:timeEntries.statusLabel')} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{t(s.i18nKey)}</MenuItem>)}
          </Select>
        </FormControl>
        {tab === 'events' && (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('attendance:timeEntries.punchLabel')}</InputLabel>
              <Select value={punchFilter} label={t('attendance:timeEntries.punchLabel')}
                onChange={(e) => setPunchFilter(e.target.value as 'all' | 'valid' | 'invalid')}>
                <MenuItem value="all">{t('attendance:timeEntries.allPunches')}</MenuItem>
                <MenuItem value="valid">{t('attendance:timeEntries.validOnly', { defaultValue: 'Valid only' })}</MenuItem>
                <MenuItem value="invalid">{t('attendance:timeEntries.invalidOnly', { defaultValue: 'Invalid only' })}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('attendance:timeEntries.sourceLabel')}</InputLabel>
              <Select value={sourceFilter} label={t('attendance:timeEntries.sourceLabel')}
                onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}>
                <MenuItem value="all">{t('attendance:timeEntries.allSources')}</MenuItem>
                <MenuItem value="mobile">{t('attendance:timeEntries.mobile')}</MenuItem>
                <MenuItem value="web">{t('attendance:timeEntries.web')}</MenuItem>
                <MenuItem value="kiosk">{t('attendance:timeEntries.kiosk')}</MenuItem>
                <MenuItem value="manual">{t('attendance:timeEntries.manual')}</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        {/* Who — free-text search last */}
        <TextField size="small" label={t('attendance:timeEntries.colWorker')} placeholder={t('attendance:timeEntries.colWorker')}
          value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
        <Button variant="contained" size="small" onClick={handleApply} disabled={!isDirty || loading}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem', minWidth: 64, height: 40 }}>
          {t('common:actions.apply')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div"
          count={tab === 'timesheets' ? entries.length : clockEvents.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Active filter chips — FILTER_DESIGN_CONVENTION §5. Only render
          non-default values. Date range is deliberately excluded (it lives
          in the Header as "truth window", not filter scope). */}
      {(() => {
        const activeChips: { key: string; label: string; onClear: () => void }[] = [];
        if (applied.loc !== 'all') {
          const name = locations.find((l) => l.id === applied.loc)?.name ?? applied.loc;
          activeChips.push({
            key: 'loc', label: `${t('attendance:timeEntries.locationLabel')}: ${name}`,
            onClear: () => {
              setLocFilter('all');
              setApplied((a) => ({ ...a, loc: 'all' }));
              setPage(0);
            },
          });
        }
        if (applied.dept !== 'all') {
          const name = depts.find((d) => d.id === applied.dept)?.name ?? applied.dept;
          activeChips.push({
            key: 'dept', label: `${t('attendance:timeEntries.departmentLabel')}: ${name}`,
            onClear: () => {
              setDeptFilter('all');
              setApplied((a) => ({ ...a, dept: 'all' }));
              setPage(0);
            },
          });
        }
        if (applied.status !== 'all') {
          const s = STATUS_OPTIONS.find((o) => o.value === applied.status);
          activeChips.push({
            key: 'status', label: `${t('attendance:timeEntries.statusLabel')}: ${s ? t(s.i18nKey) : applied.status}`,
            onClear: () => {
              setStatusFilter('all');
              setApplied((a) => ({ ...a, status: 'all' }));
              setPage(0);
            },
          });
        }
        if (applied.punch !== 'all' && tab === 'events') {
          activeChips.push({
            key: 'punch',
            label: `${t('attendance:timeEntries.punchLabel')}: ${applied.punch === 'valid' ? t('attendance:timeEntries.validOnly', { defaultValue: 'Valid only' }) : t('attendance:timeEntries.invalidOnly', { defaultValue: 'Invalid only' })}`,
            onClear: () => {
              setPunchFilter('all');
              setApplied((a) => ({ ...a, punch: 'all' }));
              setPage(0);
            },
          });
        }
        if (applied.source !== 'all' && tab === 'events') {
          const sourceLabel = applied.source === 'mobile' ? t('attendance:timeEntries.mobile')
            : applied.source === 'web' ? t('attendance:timeEntries.web')
            : applied.source === 'kiosk' ? t('attendance:timeEntries.kiosk')
            : applied.source === 'manual' ? t('attendance:timeEntries.manual')
            : applied.source;
          activeChips.push({
            key: 'source',
            label: `${t('attendance:timeEntries.sourceLabel')}: ${sourceLabel}`,
            onClear: () => {
              setSourceFilter('all');
              setApplied((a) => ({ ...a, source: 'all' }));
              setPage(0);
            },
          });
        }
        if (applied.worker) {
          activeChips.push({
            key: 'worker', label: `${t('attendance:timeEntries.colWorker')}: ${applied.worker}`,
            onClear: () => {
              setWorkerFilter('');
              setApplied((a) => ({ ...a, worker: '' }));
              const next = new URLSearchParams(searchParams);
              next.delete('worker');
              setSearchParams(next, { replace: true });
              setPage(0);
            },
          });
        }
        if (activeChips.length === 0) return null;
        return (
          <Box sx={{
            width: '100%', display: 'flex', alignItems: 'center',
            gap: 0.75, mb: 1.5, flexWrap: 'wrap',
          }}>
            {activeChips.map((c) => (
              <Chip key={c.key} label={c.label} size="small"
                onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button size="small" onClick={() => {
              setLocFilter('all');
              setDeptFilter('all');
              setStatusFilter('all');
              setPunchFilter('all');
              setSourceFilter('all');
              setWorkerFilter('');
              setApplied({
                start: applied.start, end: applied.end,
                dept: 'all', loc: 'all', status: 'all',
                punch: 'all', source: 'all', worker: '',
              });
              const next = new URLSearchParams(searchParams);
              next.delete('worker');
              setSearchParams(next, { replace: true });
              setPage(0);
            }}
              sx={{
                textTransform: 'none', fontSize: '0.75rem',
                color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}>
              {t('common:actions.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {/* Timesheets table — per-day summary */}
      {tab === 'timesheets' && (
      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('attendance:timeEntries.colWorker')}</TableCell>
              <TableCell>{t('attendance:timeEntries.colDate')}</TableCell>
              <TableCell>{t('attendance:timeEntries.colClockIn')}</TableCell>
              <TableCell>{t('attendance:timeEntries.colClockOut')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colDuration', { defaultValue: 'Hours' })}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colStatus')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colSource')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.geofence', { defaultValue: 'Geo' })}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.photo', { defaultValue: 'Photo' })}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.details', { defaultValue: 'Details' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('attendance:timeEntries.noEntriesInRange')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((e) => {
                const hasGeo = !!(e.clock_in_geo || e.clock_out_geo);
                const allInZone =
                  (!e.clock_in_geo || e.clock_in_geo.within_geofence !== false) &&
                  (!e.clock_out_geo || e.clock_out_geo.within_geofence !== false);
                const clockIn = timeOnly(e.clock_in);
                const clockOut = timeOnly(e.clock_out);
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                          {initials(e.worker_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.worker_name ?? '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(e.worker_code ?? '—')} · {e.department ?? '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption">{e.date}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: clockIn ? 600 : 400, color: clockIn ? 'text.primary' : 'text.disabled' }}>
                        {clockIn || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: clockOut ? 600 : 400, color: clockOut ? 'text.primary' : 'text.disabled' }}>
                        {clockOut || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {e.total_hours != null && e.total_hours > 0 ? fmtUnit(Number(e.total_hours), 'hour', activeLocale(), 2) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={statusDisplay(e.status, t)} size="small" color={statusColor(e.status)} variant="outlined"
                        sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip icon={sourceIcon(e.source)} label={sourceLabel(e.source)} size="small" variant="outlined"
                        sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="center">
                      {hasGeo ? (
                        <Tooltip title={allInZone ? t('attendance:timeEntries.allWithinGeofence') : t('attendance:timeEntries.outsideGeofence')}>
                          <GpsFixedOutlined sx={{ fontSize: 16, color: allInZone ? 'success.main' : 'error.main' }} />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {/* Privacy audit Wave 5 — clock-in selfie viewer.
                          Click → presigned-URL <PhotoViewerDialog>. Score
                          chip shown alongside when face matching ran. */}
                      {e.clock_in_photo_url ? (
                        <Tooltip
                          title={
                            e.clock_in_face_match_score != null
                              ? t('attendance:timeEntries.faceMatchScoreReview', { score: e.clock_in_face_match_score.toFixed(2) })
                              : t('attendance:timeEntries.viewClockInPhoto')
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => setPhotoEntry(e)}
                            sx={{
                              color: e.clock_in_face_match_score != null && e.clock_in_face_match_score < 0.7
                                ? 'warning.main'
                                : 'primary.main',
                            }}
                          >
                            <PhotoCameraOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('common:actions.viewDetails')}>
                        <IconButton size="small" onClick={() => setDetailEntry(e)}>
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Clock Events — transaction-level punch log (Kronos/UKG style).
          Each TimeEntry expands into up to 2 rows: clock-in and clock-out,
          sorted by timestamp desc. This is the SMB admin monitoring view —
          "who punched what, where, via which source, and was it in-zone". */}
      {tab === 'events' && (
      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell sx={{ width: 150 }}>{t('attendance:timeEntries.timestamp', { defaultValue: 'Timestamp' })}</TableCell>
              <TableCell>{t('attendance:timeEntries.colWorker')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colStatus')}</TableCell>
              <TableCell>{t('attendance:timeEntries.locationLabel')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colSource')}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.geofenceCol', { defaultValue: 'Geofence' })}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.photo', { defaultValue: 'Photo' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginatedEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('attendance:timeEntries.noPunchEvents')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEvents.map((ev) => {
                const ts = new Date(ev.timestamp);
                const dateStr = ts.toLocaleDateString(activeLocale());
                const timeStr = ts.toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                const inZone = ev.within_geofence !== false;
                return (
                  <TableRow key={ev.id} hover
                    sx={{ opacity: ev.is_valid ? 1 : 0.7 }}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{timeStr}</Typography>
                      <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'primary.main' }}>
                          {initials(ev.worker_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {ev.worker_name ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{ev.worker_code ?? '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={ev.is_valid ? '' : (ev.invalid_reason ?? t('attendance:timeEntries.invalidOnly', { defaultValue: 'Invalid' }))}>
                        <Chip
                          label={ev.is_valid
                            ? t('attendance:timeEntries.validPunch', { defaultValue: 'Punch' })
                            : t('attendance:timeEntries.invalidPunch', { defaultValue: 'Invalid' })}
                          size="small"
                          color={ev.is_valid ? 'success' : 'error'}
                          variant={ev.is_valid ? 'filled' : 'outlined'}
                          sx={{ borderRadius: 1, height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {ev.lat != null && ev.lng != null ? (
                        <Button
                          size="small"
                          startIcon={<LocationOnOutlined sx={{ fontSize: 14 }} />}
                          onClick={() => setLocView({ lat: ev.lat ?? null, lng: ev.lng ?? null, within: ev.within_geofence, address: ev.address, subtitle: `${ev.worker_name ?? ''} · ${timeStr}` })}
                          sx={{
                            textTransform: 'none', fontSize: '0.72rem', borderRadius: 1,
                            color: ev.within_geofence === false ? 'warning.main' : 'primary.main',
                            justifyContent: 'flex-start', maxWidth: 180,
                          }}
                        >
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ev.address ?? t('attendance:timeEntries.viewOnMap')}
                          </Box>
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip icon={sourceIcon(ev.source)} label={sourceLabel(ev.source)} size="small" variant="outlined"
                        sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="center">
                      {ev.within_geofence != null ? (
                        <Tooltip title={
                          inZone
                            ? t('attendance:timeEntries.withinGeofence')
                            : ev.geofence_exempt
                              ? t('attendance:timeEntries.outsideGeofenceApproved')
                              : t('attendance:timeEntries.outsideGeofenceLabel')
                        }>
                          {/* Outside AND approved is not an error: the server accepted
                              this punch on purpose. Left red, the admin reading this
                              table cannot tell an approved off-site day from the fence
                              having quietly stopped working — so the marker carries the
                              `info` tone the rest of the app uses for "deliberate, not
                              wrong" (business-trip days took the same colour). */}
                          <GpsFixedOutlined sx={{
                            fontSize: 16,
                            color: inZone
                              ? 'success.main'
                              : ev.geofence_exempt ? 'info.main' : 'error.main',
                          }} />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {ev.photo_url ? (
                        <Avatar variant="rounded" src={ev.photo_url ?? undefined}
                          onClick={(e) => { e.stopPropagation(); if (ev.photo_url) setPhotoUrlOverride(ev.photo_url); }}
                          sx={{ width: 30, height: 30, mx: 'auto', cursor: 'zoom-in', border: '1px solid', borderColor: 'divider' }} />
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Privacy audit Wave 5 — clock-in photo viewer (admin) */}
      <PhotoViewerDialog
        open={!!photoEntry}
        url={photoEntry?.clock_in_photo_url}
        title={photoEntry ? t('attendance:timeEntries.photoTitle', { name: photoEntry.worker_name ?? '—', date: photoEntry.date }) : undefined}
        subtitle={
          photoEntry?.clock_in_face_match_score != null
            ? t(photoEntry.clock_in_face_match_score >= 0.7
                ? 'attendance:timeEntries.faceMatchPass'
                : 'attendance:timeEntries.faceMatchBelowThreshold',
                { score: photoEntry.clock_in_face_match_score.toFixed(2) })
            : t('attendance:timeEntries.noFaceMatch')
        }
        onClose={() => setPhotoEntry(null)}
      />

      {/* ADR-028 — view a single punch's selfie (presigned URL). */}
      <PhotoViewerDialog
        open={!!photoUrlOverride}
        url={photoUrlOverride ?? undefined}
        onClose={() => setPhotoUrlOverride(null)}
      />

      {/* Location map popup — punch marker + org geofence circles + zone. */}
      <LocationViewerDialog
        open={!!locView}
        lat={locView?.lat}
        lng={locView?.lng}
        withinGeofence={locView?.within}
        address={locView?.address}
        fences={orgFences}
        subtitle={locView?.subtitle}
        onClose={() => setLocView(null)}
      />

      <Dialog open={!!detailEntry} onClose={() => setDetailEntry(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {detailEntry && (
          <>
            <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                  {initials(detailEntry.worker_name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {detailEntry.worker_name ?? '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(detailEntry.worker_code ?? '—')} · {detailEntry.department ?? '—'}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setDetailEntry(null)}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Box>
            <DialogContent sx={{ pt: 1.5 }}>
              <Stack spacing={2}>
                {/* Summary cards */}
                <Stack direction="row" spacing={1.5}>
                  {[
                    { label: t('attendance:timeEntries.colDate'), value: detailEntry.date },
                    { label: t('attendance:timeEntries.colClockIn'), value: timeOnly(detailEntry.clock_in) || '—' },
                    { label: t('attendance:timeEntries.colClockOut'), value: timeOnly(detailEntry.clock_out) || '—' },
                    {
                      label: t('attendance:timeEntries.totalHours'),
                      value:
                        detailEntry.total_hours != null && detailEntry.total_hours > 0
                          ? fmtUnit(Number(detailEntry.total_hours), 'hour', activeLocale(), 2)
                          : '—',
                    },
                  ].map((c) => (
                    <Box key={c.label} sx={{ flex: 1, py: 1.25, px: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', mb: 0.25 }}>{c.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{c.value}</Typography>
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 80px 1fr', rowGap: 1, columnGap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{t('attendance:timeEntries.statusLabel')}</Typography>
                  <Chip label={statusDisplay(detailEntry.status, t)} size="small" color={statusColor(detailEntry.status)} variant="outlined"
                    sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem', justifySelf: 'start' }} />
                  <Typography variant="caption" color="text.secondary">{t('attendance:timeEntries.sourceLabel')}</Typography>
                  <Chip icon={sourceIcon(detailEntry.source)} label={sourceLabel(detailEntry.source)} size="small" variant="outlined"
                    sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem', justifySelf: 'start' }} />
                </Box>

                {detailEntry.notes && (
                  <Box sx={{ p: 1.5, bgcolor: '#FFF8E1', borderRadius: 2, border: '1px solid', borderColor: '#FFE082' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#F57F17', display: 'block', mb: 0.25 }}>{t('attendance:timeEntries.notesSection')}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{detailEntry.notes}</Typography>
                  </Box>
                )}

                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}>
                    {t('attendance:timeEntries.geolocationSection')}
                  </Typography>
                </Box>
                <GeoDetail label={t('attendance:timeEntries.colClockIn')} time={timeOnly(detailEntry.clock_in)} geo={detailEntry.clock_in_geo}
                  onViewMap={detailEntry.clock_in_geo ? () => setLocView({ lat: detailEntry.clock_in_geo?.lat ?? null, lng: detailEntry.clock_in_geo?.lng ?? null, within: detailEntry.clock_in_geo?.within_geofence, address: detailEntry.clock_in_geo?.address, subtitle: `${detailEntry.worker_name ?? ''} · ${t('attendance:timeEntries.colClockIn')}` }) : undefined} />
                <GeoDetail label={t('attendance:timeEntries.colClockOut')} time={timeOnly(detailEntry.clock_out)} geo={detailEntry.clock_out_geo}
                  onViewMap={detailEntry.clock_out_geo ? () => setLocView({ lat: detailEntry.clock_out_geo?.lat ?? null, lng: detailEntry.clock_out_geo?.lng ?? null, within: detailEntry.clock_out_geo?.within_geofence, address: detailEntry.clock_out_geo?.address, subtitle: `${detailEntry.worker_name ?? ''} · ${t('attendance:timeEntries.colClockOut')}` }) : undefined} />

                {detailEntry.clock_in_face_status &&
                  detailEntry.clock_in_face_status !== 'not_required' && (
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem', display: 'block', mb: 0.75 }}>
                      {t('attendance:timeEntries.faceReview.section')}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: detailEntry.clock_in_face_status === 'pending_review' ? 1 : 0 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          detailEntry.clock_in_face_status === 'verified'
                            ? t('attendance:timeEntries.faceReview.statusVerified')
                            : detailEntry.clock_in_face_status === 'pending_review'
                            ? t('attendance:timeEntries.faceReview.statusPending')
                            // `pending` = still being checked, or held until the worker's face setup is
                            // approved. It used to fall through to "Failed" in red.
                            : detailEntry.clock_in_face_status === 'pending'
                            ? t('attendance:timeEntries.faceReview.statusChecking')
                            : t('attendance:timeEntries.faceReview.statusFailed')
                        }
                        color={
                          detailEntry.clock_in_face_status === 'verified'
                            ? 'success'
                            : detailEntry.clock_in_face_status === 'pending_review'
                            ? 'warning'
                            : detailEntry.clock_in_face_status === 'pending'
                            ? 'default'
                            : 'error'
                        }
                        sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem' }}
                      />
                      {detailEntry.clock_in_photo_url && (
                        <Button size="small" onClick={() => setPhotoEntry(detailEntry)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                          {t('attendance:timeEntries.faceReview.viewSelfie')}
                        </Button>
                      )}
                    </Stack>
                    {detailEntry.clock_in_face_status === 'pending_review' && (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="success" disableElevation
                          disabled={reviewingEntry === detailEntry.id}
                          onClick={() => handleReviewFace(detailEntry, 'approved')}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1 }}>
                          {t('attendance:timeEntries.faceReview.approve')}
                        </Button>
                        <Button size="small" variant="outlined" color="warning"
                          disabled={reviewingEntry === detailEntry.id}
                          onClick={() => handleReviewFace(detailEntry, 'rejected')}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1 }}>
                          {t('attendance:timeEntries.faceReview.reject')}
                        </Button>
                      </Stack>
                    )}
                  </Box>
                )}

                {/* ADR-028 — punch stream: every punch + valid/invalid reason;
                    the calc picks earliest valid = in, latest valid = out. */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem', display: 'block', mb: 0.75 }}>
                    {t('attendance:timeEntries.punches.section')}
                  </Typography>
                  {punchesLoading ? (
                    <CircularProgress size={18} />
                  ) : detailPunches.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">{t('attendance:timeEntries.punches.empty')}</Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {(() => {
                        const valid = detailPunches.filter((p) => p.is_valid);
                        const inId = valid.length ? valid[0].id : null;
                        const outId = valid.length >= 2 ? valid[valid.length - 1].id : null;
                        return detailPunches.map((p) => {
                          const tm = p.punched_at ? new Date(p.punched_at).toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit' }) : '—';
                          const role = p.id === inId ? t('attendance:timeEntries.punches.selectedIn')
                            : p.id === outId ? t('attendance:timeEntries.punches.selectedOut') : null;
                          return (
                            <Stack key={p.id} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 48 }}>{tm}</Typography>
                              {p.is_valid ? (
                                role ? (
                                  <Chip label={role} size="small" color="primary" variant="filled" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                                ) : (
                                  <Chip label={t('attendance:timeEntries.punches.valid')} size="small" color="success" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                                )
                              ) : (
                                <Chip
                                  label={t(`attendance:timeEntries.punches.reason.${p.invalid_reason ?? 'invalid'}`)}
                                  size="small" color="default" variant="outlined"
                                  sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem', textDecoration: 'line-through', opacity: 0.7 }}
                                />
                              )}
                              {p.within_geofence === false && (
                                <Chip icon={<WarningAmberOutlined sx={{ fontSize: 12 }} />} label={t('attendance:timeEntries.outOfZone')} size="small" color="warning" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.62rem' }} />
                              )}
                              {p.face_status && p.face_status !== 'not_required' && (
                                <Chip label={t(`attendance:timeEntries.punches.face.${p.face_status}`)} size="small" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.62rem' }} />
                              )}
                              {p.photo_url && (
                                <Button size="small" onClick={() => setPhotoUrlOverride(p.photo_url ?? null)} sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, p: 0.25 }}>
                                  {t('attendance:timeEntries.faceReview.viewSelfie')}
                                </Button>
                              )}
                            </Stack>
                          );
                        });
                      })()}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Manual entry dialog — backs "Add Manual Entry" header button. */}
      <Dialog open={manualOpen} onClose={() => !manualSaving && setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('attendance:timeEntries.addManualEntryTitle')}</Typography>
            <IconButton size="small" onClick={() => !manualSaving && setManualOpen(false)}>
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('attendance:timeEntries.manualEntryHint')}
            </Typography>
            <Autocomplete
              size="small"
              options={workers}
              getOptionLabel={(e) => `${e.first_name} ${e.last_name} (${e.worker_code})`}
              value={workers.find((e) => e.id === manual.worker_id) ?? null}
              onChange={(_, v) => setManual((m) => ({ ...m, worker_id: v?.id ?? '' }))}
              renderInput={(params) => (
                <TextField {...params} label={t('attendance:timeEntries.colWorker')} required />
              )}
            />
            <DateField label={t('attendance:timeEntries.colDate')} size="small" required
              value={manual.date}
              onChange={(e) => setManual((m) => ({ ...m, date: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <TextField label={t('attendance:timeEntries.clockInTimeLabel')} type="time" size="small" required
                InputLabelProps={{ shrink: true }}
                value={manual.clock_in_time}
                onChange={(e) => setManual((m) => ({ ...m, clock_in_time: e.target.value }))}
                sx={{ flex: 1 }} />
              <TextField label={t('attendance:timeEntries.clockOutTimeLabel')} type="time" size="small"
                InputLabelProps={{ shrink: true }}
                value={manual.clock_out_time}
                onChange={(e) => setManual((m) => ({ ...m, clock_out_time: e.target.value }))}
                sx={{ flex: 1 }} />
            </Stack>
            {manual.clock_out_time && manual.clock_out_time <= manual.clock_in_time && (
              <Alert severity="warning" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                {t('attendance:timeEntries.clockOutBeforeIn')}
              </Alert>
            )}
            <TextField label={t('attendance:timeEntries.notesLabel')} multiline rows={2} size="small" fullWidth
              placeholder={t('attendance:timeEntries.notesPlaceholder')}
              value={manual.notes}
              onChange={(e) => setManual((m) => ({ ...m, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualSaving}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveManual} disabled={!canSaveManual || manualSaving}>
            {manualSaving ? <CircularProgress size={18} /> : t('attendance:timeEntries.saveEntry')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeEntries;
